import type {
  MatchSelection,
  OutrightSelection,
  SportCompetitor,
} from "@/features/betslip/types";
import type {
  MatchToggleInput,
  OutrightToggleInput,
} from "@/features/betslip/builders/types";
import { getActualCompetitorId } from "@/features/betslip/utils/selectionHelpers";

type SportSingleInput = MatchToggleInput | OutrightToggleInput;

/**
 * Builds the fields that match and outright share. Per-kind wrappers
 * below stamp on `kind` so callers receive the correct discriminated
 * type back.
 */
const buildSportSingleFields = (input: SportSingleInput) => {
  const { event, market, outcome, outcomeId, hub, originType, originId } =
    input;

  // Sport competitors live on the event keyed by id. If the lookup
  // misses we leave competitor undefined — consumers handle that.
  const rawCompetitor = outcome.competitorId
    ? event.competitors?.[outcome.competitorId]
    : undefined;
  const competitor: SportCompetitor | undefined =
    rawCompetitor && outcome.competitorId
      ? { ...rawCompetitor, id: outcome.competitorId }
      : undefined;

  return {
    outcomeId,
    eventId: event.id,
    marketId: market.id,
    selectionName: outcome.name ?? "",
    eventName: event.eventName ?? "",
    marketName: market.name ?? "",
    sport: event.sport ?? "",
    startTime: market.nextBetStop ?? event.scheduledStartTime ?? new Date(),
    hub,
    marketType: market.marketType ?? "",
    odds: outcome.odds ?? 0,
    isSP: market.marketType?.endsWith("_SP"),
    isMultiable: market.attributes?.multiable ?? true,
    independentPerEvent: market.independentPerEvent ?? false,
    filters: event.filters ?? [],
    blockPromotionsBets: !!market.blockPromotionsBets,
    hasPromotionalMarkets: !!market.hasPromotionalMarkets,
    hasLiveMarkets: market.bettingType === "Live",
    originType: originType ?? "BETTING",
    originId,
    competitor,
    competitorId: getActualCompetitorId(outcome),
    outcomeGroup: outcome.outcomeGroup,
    // outcomeType drives the SportOutcomeIcon glyph.
    outcomeType: outcome.type ?? undefined,
    seasonId: event.seasonId,
    tournamentId: event.tournamentId,
    marketStatus: market.status,
    eventStatus: event.status,
    streamUri: event.streamUri ?? undefined,
    // Stream player back-link to the match page.
    eventUrl: `${hub === "sports" ? "/sports" : ""}/betting/match/${event.eventId}`,
  };
};

export const buildMatchSelection = (
  input: MatchToggleInput,
): MatchSelection => ({
  kind: "match",
  ...buildSportSingleFields(input),
});

export const buildOutrightSelection = (
  input: OutrightToggleInput,
): OutrightSelection => ({
  kind: "outright",
  ...buildSportSingleFields(input),
});
